/**
 * Agenda auto-timing (spec US-MB-3/4 / task 2.5). Pure: given ordered blocks and
 * an optional start clock, compute each block's start offset + clock time, the
 * running total, and the end clock. Reordering/adding/removing then recomputing
 * is just calling this again — the planner UI holds the order, this owns the math.
 *
 * Plus the two halves of the block↔round join (US-MB-6 fatia 2), here because BOTH apps need the
 * same answer: `validateBlockId` is the shape kit-api's store accepts as a block's `client_key`,
 * and `roundsForBlock` is how the planner reads a closed round back out. Same reason
 * `roundConfigFor` lives in kit-core — one rule, used by the launcher, the store and the cross-app
 * test.
 */
import type { AgendaBlockInput, ScheduledAgenda, ScheduledBlock, SharedAgendaWire } from "./types.js";

const MINUTES_PER_DAY = 24 * 60;

/**
 * A block key kit-api will store in `agenda_blocks.client_key` — exactly what `crypto.randomUUID()`
 * emits.
 *
 * It is NOT the primary key: `agenda_blocks.id` is global, and this string is PUBLISHED (it is the
 * `<blockId>.` prefix of `state.currentQuestionId`, which every socket on the room receives,
 * authenticated or not). A published value must never be a global key, so 0009 scopes it to one
 * agenda with a UNIQUE(agenda_id, client_key) index and the server keeps minting `id` itself.
 *
 * The shape rule survives that redesign for a different reason than secrecy: one canonical spelling.
 * Uppercase hex, braces, a `urn:uuid:` prefix and any other length are refused rather than
 * normalised, because two spellings of one uuid would be two rows in the agenda and one prefix in
 * `session_rounds` — the join would silently show a block half its rounds.
 */
const BLOCK_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

export function validateBlockId(value: unknown): value is string {
  return typeof value === "string" && BLOCK_ID.test(value);
}

/**
 * Does this round id belong to this block? The inverse of the planner's `qidFor`, as ONE predicate.
 *
 * `question_id` is stored verbatim by live-api, and the planner writes `<blockId>.<8 hex>` into it
 * — `<blockId>` being the planner's own key for the block, which is what `agenda_blocks.client_key`
 * keeps — so a block's rounds are the exact match plus everything under `<blockId>.`. The dot
 * matters: a
 * bare `startsWith(blockId)` would make block `abc` claim `abcd.1234`'s round — different block,
 * same prefix — which is why the separator is part of the test and not just of the id.
 *
 * Extracted in fatia 3 because a SECOND caller arrived: the planner asks the same question of a
 * `closed` frame's `questionId` (which block just closed?) that `roundsForBlock` asks of a stored
 * round. Two spellings of this rule would drift, and the one that drifted would silently arm the
 * wrong block.
 */
export function isRoundOfBlock(questionId: string, blockId: string): boolean {
  // An empty block id would otherwise match every round on the `startsWith` arm.
  return blockId !== "" && (questionId === blockId || questionId.startsWith(`${blockId}.`));
}

/**
 * The rounds a block ran, newest first — `isRoundOfBlock` applied to a list.
 *
 * Generic over the row: kit-core deliberately does not model live-api's wire types, so this asks
 * for the two fields it reads and nothing else. Newest first by `closedAt` (a round is listed by
 * when it ENDED, which is the order a facilitator scrolls), `ord` breaking the tie for two rounds
 * that closed in the same second.
 */
export function roundsForBlock<T extends { questionId: string; closedAt: number; ord?: number }>(
  rounds: readonly T[],
  blockId: string,
): T[] {
  return rounds
    .filter((r) => isRoundOfBlock(r.questionId, blockId))
    .sort((a, b) => b.closedAt - a.closedAt || (b.ord ?? 0) - (a.ord ?? 0));
}

/** Clamp a duration to a non-negative integer (guards NaN / negatives). */
export function normalizeDuration(min: number): number {
  if (!Number.isFinite(min) || min < 0) return 0;
  return Math.round(min);
}

export function parseClock(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

export function formatClock(totalMin: number): string {
  const wrapped = ((totalMin % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function recomputeAgenda(blocks: readonly AgendaBlockInput[], startClock?: string): ScheduledAgenda {
  const startMin = startClock ? parseClock(startClock) : null;
  let offset = 0;
  const scheduled: ScheduledBlock[] = blocks.map((b, i) => {
    const duration = normalizeDuration(b.durationMin);
    const block: ScheduledBlock = {
      ...b,
      durationMin: duration,
      order: i,
      startOffsetMin: offset,
    };
    if (startMin !== null) block.startClock = formatClock(startMin + offset);
    offset += duration;
    return block;
  });

  const agenda: ScheduledAgenda = { blocks: scheduled, totalMin: offset };
  if (startMin !== null) agenda.endClock = formatClock(startMin + offset);
  return agenda;
}

/**
 * The schedule a VISITOR sees (THE-1698): the whole plan is timed first, then the blocks marked
 * `facilitatorOnly` are dropped.
 *
 * Timed BEFORE filtering on purpose. Re-timing only the visible blocks would print clock times that
 * are wrong on the day (a 10-minute facilitator-only block at 10:00 would make the next block read
 * 10:00 instead of 10:10). The cost is that a gap and the total reveal how much time was held back,
 * never what it was for — the title, method and anything else about the block are gone.
 */
export function visitorAgenda(blocks: readonly AgendaBlockInput[], startClock?: string): ScheduledAgenda {
  const full = recomputeAgenda(blocks, startClock);
  return { ...full, blocks: full.blocks.filter((b) => b.facilitatorOnly !== true) };
}

/**
 * A `ScheduledAgenda` rebuilt from what the share link returned — the ONE input the visitor's page,
 * its print view and its CSV all render from, so they cannot disagree about what a visitor may see.
 */
export function sharedSchedule(shared: SharedAgendaWire): ScheduledAgenda {
  const startMin = shared.startClock ? parseClock(shared.startClock) : null;
  const blocks: ScheduledBlock[] = shared.blocks.map((b, i) => {
    const block: ScheduledBlock = {
      id: String(i),
      title: b.title,
      durationMin: normalizeDuration(b.durationMin),
      order: i,
      startOffsetMin: b.startOffsetMin,
    };
    if (startMin !== null) block.startClock = formatClock(startMin + b.startOffsetMin);
    return block;
  });
  const agenda: ScheduledAgenda = { blocks, totalMin: shared.totalMin };
  if (startMin !== null) agenda.endClock = formatClock(startMin + shared.totalMin);
  return agenda;
}
